import React, { useState, useEffect, useRef } from 'react';
import { 
  Plus, Bell, BellOff, Trash2, Settings, Volume2, 
  Footprints, Clock, ChevronRight, X, Upload, Play, 
  Pause, Check, Moon, Sun, Repeat, Download, Smartphone,
  Vibrate, Info, ChevronLeft, AlarmClock, TrendingUp,
  Zap, Target, Calendar, BarChart3, Award, Sliders,
  Users, Trophy, Swords, Heart, Share2, Copy, UserPlus,
  Brain, Sparkles, AlertCircle, CheckCircle, Flame
} from 'lucide-react';
import './App.css';

import { GoogleLogin } from '@react-oauth/google';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// ============== UTILITY FUNCTIONS ==============

// Request notification permission
const requestNotificationPermission = async () => {
  if (!('Notification' in window)) {
    console.log('Notifications not supported');
    return false;
  }
  
  if (Notification.permission === 'granted') {
    return true;
  }
  
  if (Notification.permission !== 'denied') {
    const permission = await Notification.requestPermission();
    return permission === 'granted';
  }
  
  return false;
};

// Send notification
const sendNotification = (title, body, tag = 'walkup-alarm') => {
  if (Notification.permission === 'granted') {
    const options = {
      body,
      icon: '/logo192.png',
      badge: '/logo192.png',
      tag,
      requireInteraction: false,
      silent: false,
      vibrate: [200, 100, 200]
    };
    
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.ready.then((registration) => {
        registration.showNotification(title, options);
      });
    } else {
      new Notification(title, options);
    }
  }
};

// ============== COMPONENTS ==============

// Time Wheel Picker Component
const TimeWheelPicker = ({ value, onChange, max, padZero = true }) => {
  const containerRef = useRef(null);
  const [items] = useState(() => Array.from({ length: max }, (_, i) => i));
  
  useEffect(() => {
    if (containerRef.current) {
      const itemHeight = 50;
      containerRef.current.scrollTop = value * itemHeight;
    }
  }, []);

  const handleScroll = () => {
    if (containerRef.current) {
      const itemHeight = 50;
      const scrollTop = containerRef.current.scrollTop;
      const newValue = Math.round(scrollTop / itemHeight);
      if (newValue !== value && newValue >= 0 && newValue < max) {
        onChange(newValue);
      }
    }
  };

  return (
    <div 
      ref={containerRef}
      className="time-wheel scrollbar-hide"
      onScroll={handleScroll}
      data-testid={`time-wheel-${max === 24 ? 'hour' : 'minute'}`}
    >
      <div style={{ height: '75px' }} />
      {items.map((item) => (
        <div
          key={item}
          className={`time-wheel-item font-display ${item === value ? 'selected' : ''}`}
        >
          {padZero ? String(item).padStart(2, '0') : item}
        </div>
      ))}
      <div style={{ height: '75px' }} />
    </div>
  );
};

// Day Selector Component
const DaySelector = ({ selectedDays, onChange }) => {
  const days = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
  
  const toggleDay = (index) => {
    const newDays = selectedDays.includes(index)
      ? selectedDays.filter(d => d !== index)
      : [...selectedDays, index].sort((a, b) => a - b);
    onChange(newDays);
  };

  return (
    <div className="flex justify-between gap-2">
      {days.map((day, index) => (
        <button
          key={index}
          className={`day-pill tap-scale ${selectedDays.includes(index) ? 'active' : ''}`}
          onClick={() => toggleDay(index)}
          data-testid={`day-pill-${index}`}
        >
          {day}
        </button>
      ))}
    </div>
  );
};

// Switch Component
const Switch = ({ checked, onChange, testId }) => (
  <div
    className={`switch tap-scale ${checked ? 'active' : ''}`}
    onClick={() => onChange(!checked)}
    data-testid={testId}
    role="switch"
    aria-checked={checked}
  />
);

// Alarm Card Component
const AlarmCard = ({ alarm, onToggle, onEdit }) => {
  const formatTime = (time) => {
    const [hours, minutes] = time.split(':');
    const h = parseInt(hours);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const displayHour = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return { time: `${displayHour}:${minutes}`, ampm };
  };

  const getDaysText = (days) => {
    if (days.length === 0) return 'Once';
    if (days.length === 7) return 'Every day';
    if (JSON.stringify(days) === JSON.stringify([1, 2, 3, 4, 5])) return 'Weekdays';
    if (JSON.stringify(days) === JSON.stringify([0, 6])) return 'Weekends';
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return days.map(d => dayNames[d]).join(', ');
  };

  const { time, ampm } = formatTime(alarm.time);

  return (
    <div 
      className="bg-[#1C1C1E] rounded-[24px] p-6 tap-scale cursor-pointer border border-white/5"
      onClick={() => onEdit(alarm)}
      data-testid={`alarm-card-${alarm.id}`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-baseline gap-2">
          <span className={`font-display text-5xl font-semibold ${alarm.enabled ? 'text-white' : 'text-[#3A3A3C]'}`}>
            {time}
          </span>
          <span className={`text-lg font-medium ${alarm.enabled ? 'text-[#8E8E93]' : 'text-[#3A3A3C]'}`}>
            {ampm}
          </span>
        </div>
        <div onClick={(e) => e.stopPropagation()}>
          <Switch 
            checked={alarm.enabled} 
            onChange={(checked) => onToggle(alarm.id, checked)}
            testId={`alarm-toggle-${alarm.id}`}
          />
        </div>
      </div>
      <div className="flex items-center gap-3 text-sm text-[#8E8E93]">
        {alarm.label && <span>{alarm.label}</span>}
        {alarm.label && <span>·</span>}
        <span>{getDaysText(alarm.recurring_days)}</span>
        <span>·</span>
        <span className="flex items-center gap-1">
          <Footprints size={14} />
          {alarm.steps_required}
        </span>
      </div>
    </div>
  );
};

// Create/Edit Alarm Modal
const AlarmModal = ({ alarm, onSave, onClose, onDelete, sounds }) => {
  const isEdit = !!alarm?.id;
  const [hour, setHour] = useState(alarm ? parseInt(alarm.time.split(':')[0]) : 7);
  const [minute, setMinute] = useState(alarm ? parseInt(alarm.time.split(':')[1]) : 0);
  const [label, setLabel] = useState(alarm?.label || '');
  const [recurringDays, setRecurringDays] = useState(alarm?.recurring_days || []);
  const [stepsRequired, setStepsRequired] = useState(alarm?.steps_required || 50);
  const [snoozeLimit, setSnoozeLimit] = useState(alarm?.snooze_limit || 3);
  const [snoozeInterval, setSnoozeInterval] = useState(alarm?.snooze_interval || 5);
  const [soundId, setSoundId] = useState(alarm?.sound_id || null);
  const [vibrate, setVibrate] = useState(alarm?.vibrate !== false);

  const handleSave = () => {
    const time = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
    onSave({
      ...alarm,
      time,
      label,
      recurring_days: recurringDays,
      steps_required: stepsRequired,
      snooze_limit: snoozeLimit,
      snooze_interval: snoozeInterval,
      sound_id: soundId,
      vibrate,
      enabled: alarm?.enabled !== false,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-end justify-center">
      <div 
        className="bottom-sheet animate-slide-up w-full" 
        data-testid="alarm-modal"
      >
        <div className="bottom-sheet-header flex items-center justify-between">
          <button 
            className="text-[#FF453A] font-medium text-base min-w-[60px]" 
            onClick={onClose}
            data-testid="alarm-modal-cancel"
          >
            Cancel
          </button>
          <span className="font-display font-semibold text-lg">
            {isEdit ? 'Edit Alarm' : 'Add Alarm'}
          </span>
          <button 
            className="text-[#FF453A] font-medium text-base min-w-[60px] text-right" 
            onClick={handleSave}
            data-testid="alarm-modal-save"
          >
            Save
          </button>
        </div>

        <div className="bottom-sheet-content">
          <div className="flex items-center justify-center gap-4 mb-8 bg-[#2C2C2E] rounded-[20px] py-4 relative">
            <TimeWheelPicker value={hour} onChange={setHour} max={24} />
            <span className="font-display text-5xl font-semibold text-white">:</span>
            <TimeWheelPicker value={minute} onChange={setMinute} max={60} />
            <div className="absolute inset-x-4 top-1/2 -translate-y-1/2 h-[50px] border-t border-b border-white/10 pointer-events-none" />
          </div>

          <div className="mb-6">
            <label className="text-sm text-[#8E8E93] uppercase tracking-wide mb-2 block">Label</label>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Wake up!"
              className="w-full bg-[#2C2C2E] rounded-xl p-4 text-white placeholder-[#3A3A3C] border-none outline-none focus:ring-2 focus:ring-[#FF453A]/50"
              data-testid="alarm-label-input"
            />
          </div>

          <div className="mb-6">
            <label className="text-sm text-[#8E8E93] uppercase tracking-wide mb-3 block">Repeat</label>
            <DaySelector selectedDays={recurringDays} onChange={setRecurringDays} />
          </div>

          <div className="mb-6">
            <label className="text-sm text-[#8E8E93] uppercase tracking-wide mb-2 block">Steps to Dismiss</label>
            <div className="flex items-center gap-4">
              <input
                type="range"
                min="10"
                max="200"
                step="10"
                value={stepsRequired}
                onChange={(e) => setStepsRequired(parseInt(e.target.value))}
                className="flex-1 accent-[#FF453A]"
                data-testid="steps-slider"
              />
              <span className="font-display text-2xl font-semibold text-white w-16 text-right">
                {stepsRequired}
              </span>
            </div>
          </div>

          <div className="mb-6 grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-[#8E8E93] uppercase tracking-wide mb-2 block">Snooze Limit</label>
              <select
                value={snoozeLimit}
                onChange={(e) => setSnoozeLimit(parseInt(e.target.value))}
                className="w-full bg-[#2C2C2E] rounded-xl p-4 text-white border-none outline-none"
                data-testid="snooze-limit-select"
              >
                {[1, 2, 3, 4, 5, 10].map(n => (
                  <option key={n} value={n}>{n} times</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm text-[#8E8E93] uppercase tracking-wide mb-2 block">Snooze Interval</label>
              <select
                value={snoozeInterval}
                onChange={(e) => setSnoozeInterval(parseInt(e.target.value))}
                className="w-full bg-[#2C2C2E] rounded-xl p-4 text-white border-none outline-none"
                data-testid="snooze-interval-select"
              >
                {[1, 2, 3, 5, 10, 15].map(n => (
                  <option key={n} value={n}>{n} min</option>
                ))}
              </select>
            </div>
          </div>

          <div className="mb-6">
            <label className="text-sm text-[#8E8E93] uppercase tracking-wide mb-2 block">Alarm Sound</label>
            <select
              value={soundId || ''}
              onChange={(e) => setSoundId(e.target.value || null)}
              className="w-full bg-[#2C2C2E] rounded-xl p-4 text-white border-none outline-none"
              data-testid="sound-select"
            >
              <option value="">Default Sound</option>
              {sounds.map(sound => (
                <option key={sound.id} value={sound.id}>{sound.name}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center justify-between p-4 bg-[#2C2C2E] rounded-xl mb-6">
            <div className="flex items-center gap-3">
              <Vibrate size={20} className="text-[#8E8E93]" />
              <span>Vibrate</span>
            </div>
            <Switch checked={vibrate} onChange={setVibrate} testId="vibrate-toggle" />
          </div>

          {isEdit && (
            <button
              onClick={() => onDelete(alarm.id)}
              className="w-full py-4 bg-[#2C2C2E] rounded-xl text-[#FF453A] font-medium flex items-center justify-center gap-2"
              data-testid="delete-alarm-button"
            >
              <Trash2 size={20} />
              Delete Alarm
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Active Alarm Screen with Snooze Button and History Tracking
const ActiveAlarmScreen = ({ alarm, onDismiss, onSnooze, sounds, motionCalibration }) => {
  const [steps, setSteps] = useState(0);
  const [isShaking, setIsShaking] = useState(false);
  const [snoozeCount, setSnoozeCount] = useState(0);
  const [startTime] = useState(Date.now());
  const targetSteps = alarm.steps_required;
  const progress = Math.min((steps / targetSteps) * 100, 100);
  const audioRef = useRef(null);
  const audioElementRef = useRef(null);
  const canSnooze = snoozeCount < alarm.snooze_limit;

  // Motion calibration settings
  const sensitivity = motionCalibration?.sensitivity || 12;
  const minInterval = motionCalibration?.min_interval || 300;

  useEffect(() => {
    const requestMotion = async () => {
      if (typeof DeviceMotionEvent !== 'undefined' && typeof DeviceMotionEvent.requestPermission === 'function') {
        try {
          const permission = await DeviceMotionEvent.requestPermission();
          if (permission === 'granted') {
            startStepTracking();
          }
        } catch (e) {
          console.log('Motion permission denied');
          startStepTracking();
        }
      } else {
        startStepTracking();
      }
    };

    let lastAccel = null;
    let lastStepTime = 0;

    const handleMotion = (event) => {
      const { accelerationIncludingGravity } = event;
      if (!accelerationIncludingGravity) return;

      const { x, y, z } = accelerationIncludingGravity;
      const accel = Math.sqrt(x * x + y * y + z * z);

      if (lastAccel !== null) {
        const delta = Math.abs(accel - lastAccel);
        const now = Date.now();

        if (delta > sensitivity && now - lastStepTime > minInterval) {
          lastStepTime = now;
          setSteps(prev => {
            const newSteps = prev + 1;
            if (newSteps >= targetSteps) {
              window.removeEventListener('devicemotion', handleMotion);
              handleDismiss();
            }
            return newSteps;
          });
          setIsShaking(true);
          setTimeout(() => setIsShaking(false), 100);
        }
      }
      lastAccel = accel;
    };

    const startStepTracking = () => {
      window.addEventListener('devicemotion', handleMotion);
    };

    requestMotion();
    playAlarmSound();

    return () => {
      window.removeEventListener('devicemotion', handleMotion);
      stopAudio();
    };
  }, [targetSteps, sensitivity, minInterval]);

  const handleDismiss = () => {
    const timeToDismiss = Math.round((Date.now() - startTime) / 1000);
    stopAudio();
    onDismiss({
      alarm_id: alarm.id,
      alarm_label: alarm.label || '',
      alarm_time: alarm.time,
      action: 'dismissed',
      steps_taken: steps >= targetSteps ? steps : targetSteps,
      snooze_count: snoozeCount,
      time_to_dismiss: timeToDismiss,
    });
  };

  const handleSnooze = () => {
    if (!canSnooze) return;
    stopAudio();
    setSnoozeCount(prev => prev + 1);
    onSnooze({
      alarm_id: alarm.id,
      alarm_label: alarm.label || '',
      alarm_time: alarm.time,
      action: 'snoozed',
      steps_taken: steps,
      snooze_count: snoozeCount + 1,
      time_to_dismiss: 0,
    }, alarm.snooze_interval);
  };

  const playAlarmSound = async () => {
    if (alarm.sound_id && sounds) {
      const sound = sounds.find(s => s.id === alarm.sound_id);
      if (sound) {
        try {
          const res = await fetch(`${API_URL}/api/sounds/${alarm.sound_id}`);
          const data = await res.json();
          if (data.sound && data.sound.data) {
            const audio = new Audio(`data:${data.sound.mime_type};base64,${data.sound.data}`);
            audio.loop = true;
            audio.play().catch(e => console.log('Audio play failed:', e));
            audioElementRef.current = audio;
            return;
          }
        } catch (err) {
          console.error('Failed to load custom sound:', err);
        }
      }
    }

    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    oscillator.frequency.value = 880;
    oscillator.type = 'sine';
    gainNode.gain.value = 0.3;
    oscillator.start();
    
    const interval = setInterval(() => {
      gainNode.gain.value = gainNode.gain.value === 0 ? 0.3 : 0;
    }, 500);

    audioRef.current = { oscillator, audioContext, interval };
  };

  const stopAudio = () => {
    if (audioElementRef.current) {
      audioElementRef.current.pause();
      audioElementRef.current = null;
    }
    if (audioRef.current) {
      if (audioRef.current.oscillator) {
        try { audioRef.current.oscillator.stop(); } catch(e) {}
      }
      if (audioRef.current.interval) {
        clearInterval(audioRef.current.interval);
      }
      if (audioRef.current.audioContext) {
        audioRef.current.audioContext.close();
      }
      audioRef.current = null;
    }
  };

  const simulateStep = () => {
    setSteps(prev => {
      const newSteps = prev + 1;
      if (newSteps >= targetSteps) {
        setTimeout(handleDismiss, 500);
      }
      return newSteps;
    });
    setIsShaking(true);
    setTimeout(() => setIsShaking(false), 100);
  };

  const formatTime = (time) => {
    const [hours, minutes] = time.split(':');
    const h = parseInt(hours);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const displayHour = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const circumference = 2 * Math.PI * 120;
  const strokeDashoffset = circumference - (progress / 100) * circumference;

  return (
    <div className="active-alarm-screen" data-testid="active-alarm-screen">
      <div className="text-center mb-6">
        <p className="text-[#8E8E93] text-sm uppercase tracking-widest mb-2">Wake Up</p>
        <h1 className="font-display text-6xl font-semibold gradient-text">
          {formatTime(alarm.time)}
        </h1>
        {alarm.label && (
          <p className="text-[#8E8E93] mt-2">{alarm.label}</p>
        )}
      </div>

      <div className="orb-container mb-6" onClick={simulateStep}>
        <div className="orb-glow animate-pulse-glow" />
        <img 
          src="https://static.prod-images.emergentagent.com/jobs/27c46002-d378-4125-9f9d-a812574463dc/images/cee7f8a595119cadab9b8e1195d8a438918954e60dc65016e56cb9157788fee4.png"
          alt="Alarm"
          className="orb-image"
        />
        <svg className="absolute inset-0 w-full h-full progress-ring" viewBox="0 0 280 280">
          <circle cx="140" cy="140" r="120" fill="none" stroke="rgba(255, 255, 255, 0.1)" strokeWidth="4" />
          <circle
            cx="140" cy="140" r="120" fill="none" stroke="url(#gradient)" strokeWidth="4"
            strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={strokeDashoffset}
            className="progress-ring-circle"
          />
          <defs>
            <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#FF453A" />
              <stop offset="100%" stopColor="#FF9F0A" />
            </linearGradient>
          </defs>
        </svg>
      </div>

      <div className={`text-center mb-8 ${isShaking ? 'animate-shake' : ''}`}>
        <div className="flex items-center justify-center gap-3 mb-2">
          <Footprints size={32} className="text-[#FF9F0A]" />
          <span className="font-display text-7xl font-bold text-white" data-testid="step-count">
            {steps}
          </span>
          <span className="font-display text-2xl text-[#8E8E93] self-end mb-2">
            / {targetSteps}
          </span>
        </div>
        <p className="text-[#8E8E93]">Walk to dismiss alarm</p>
        <p className="text-[#3A3A3C] text-xs mt-2">(Tap orb to simulate steps)</p>
      </div>

      {/* Snooze Button */}
      <div className="px-8 w-full max-w-sm mx-auto">
        <button
          onClick={handleSnooze}
          disabled={!canSnooze}
          className={`w-full py-4 rounded-full font-semibold text-lg flex items-center justify-center gap-3 transition-all ${
            canSnooze 
              ? 'bg-[#2C2C2E] text-white hover:bg-[#3A3A3C]' 
              : 'bg-[#1C1C1E] text-[#3A3A3C] cursor-not-allowed'
          }`}
          data-testid="snooze-button"
        >
          <AlarmClock size={24} />
          {canSnooze 
            ? `Snooze ${alarm.snooze_interval} min (${alarm.snooze_limit - snoozeCount} left)`
            : 'No more snoozes'
          }
        </button>
      </div>
    </div>
  );
};

// Statistics Page Component
const StatisticsPage = ({ onBack }) => {
  const [statistics, setStatistics] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStatistics();
    fetchHistory();
  }, []);

  const fetchStatistics = async () => {
    try {
      const res = await fetch(`${API_URL}/api/statistics`);
      const data = await res.json();
      setStatistics(data.statistics);
    } catch (err) {
      console.error('Failed to fetch statistics:', err);
    }
  };

  const fetchHistory = async () => {
    try {
      const res = await fetch(`${API_URL}/api/history?days=7`);
      const data = await res.json();
      setHistory(data.history || []);
    } catch (err) {
      console.error('Failed to fetch history:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const formatTime = (timeStr) => {
    const [hours, minutes] = timeStr.split(':');
    const h = parseInt(hours);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const displayHour = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const getActionColor = (action) => {
    switch(action) {
      case 'dismissed': return 'text-[#32D74B]';
      case 'snoozed': return 'text-[#FF9F0A]';
      case 'missed': return 'text-[#FF453A]';
      default: return 'text-[#8E8E93]';
    }
  };

  const getActionIcon = (action) => {
    switch(action) {
      case 'dismissed': return <Check size={16} />;
      case 'snoozed': return <AlarmClock size={16} />;
      case 'missed': return <X size={16} />;
      default: return null;
    }
  };

  if (loading) {
    return (
      <div className="app-container flex items-center justify-center">
        <div className="text-[#8E8E93]">Loading statistics...</div>
      </div>
    );
  }

  const weekly = statistics?.weekly || {};

  return (
    <div className="app-container pb-32">
      <header className="pt-12 px-6 pb-6">
        <div className="flex items-center gap-4">
          <button onClick={onBack} className="p-2 -ml-2 text-[#FF453A]" data-testid="stats-back">
            <ChevronLeft size={28} />
          </button>
          <h1 className="font-display text-2xl font-semibold">Statistics</h1>
        </div>
      </header>

      <main className="px-6">
        {/* Streak Card */}
        <div className="bg-gradient-to-br from-[#FF453A] to-[#FF9F0A] rounded-[24px] p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/80 text-sm uppercase tracking-wide">Current Streak</p>
              <p className="font-display text-5xl font-bold text-white mt-1">
                {statistics?.streak || 0} days
              </p>
            </div>
            <Award size={48} className="text-white/80" />
          </div>
        </div>

        {/* Weekly Stats Grid */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="bg-[#1C1C1E] rounded-[20px] p-4">
            <div className="flex items-center gap-2 mb-2">
              <Target size={18} className="text-[#32D74B]" />
              <span className="text-[#8E8E93] text-sm">Success Rate</span>
            </div>
            <p className="font-display text-3xl font-semibold text-white">
              {weekly.success_rate || 0}%
            </p>
          </div>
          <div className="bg-[#1C1C1E] rounded-[20px] p-4">
            <div className="flex items-center gap-2 mb-2">
              <Footprints size={18} className="text-[#FF9F0A]" />
              <span className="text-[#8E8E93] text-sm">Total Steps</span>
            </div>
            <p className="font-display text-3xl font-semibold text-white">
              {weekly.total_steps || 0}
            </p>
          </div>
          <div className="bg-[#1C1C1E] rounded-[20px] p-4">
            <div className="flex items-center gap-2 mb-2">
              <Check size={18} className="text-[#32D74B]" />
              <span className="text-[#8E8E93] text-sm">Dismissed</span>
            </div>
            <p className="font-display text-3xl font-semibold text-white">
              {weekly.dismissed || 0}
            </p>
          </div>
          <div className="bg-[#1C1C1E] rounded-[20px] p-4">
            <div className="flex items-center gap-2 mb-2">
              <AlarmClock size={18} className="text-[#FF9F0A]" />
              <span className="text-[#8E8E93] text-sm">Snoozed</span>
            </div>
            <p className="font-display text-3xl font-semibold text-white">
              {weekly.snoozed || 0}
            </p>
          </div>
        </div>

        {/* Average Stats */}
        <div className="bg-[#1C1C1E] rounded-[20px] p-4 mb-6">
          <h3 className="text-sm text-[#8E8E93] uppercase tracking-wide mb-4">Averages This Week</h3>
          <div className="flex justify-between">
            <div className="text-center">
              <p className="font-display text-2xl font-semibold text-white">{weekly.avg_steps_per_alarm || 0}</p>
              <p className="text-xs text-[#8E8E93]">Steps/Alarm</p>
            </div>
            <div className="text-center">
              <p className="font-display text-2xl font-semibold text-white">
                {Math.round((weekly.avg_time_to_dismiss_seconds || 0) / 60)}m
              </p>
              <p className="text-xs text-[#8E8E93]">Time to Wake</p>
            </div>
            <div className="text-center">
              <p className="font-display text-2xl font-semibold text-white">{weekly.snooze_rate || 0}%</p>
              <p className="text-xs text-[#8E8E93]">Snooze Rate</p>
            </div>
          </div>
        </div>

        {/* History */}
        <h3 className="text-sm text-[#8E8E93] uppercase tracking-wide mb-3">Recent History</h3>
        {history.length === 0 ? (
          <div className="bg-[#1C1C1E] rounded-[20px] p-6 text-center">
            <BarChart3 size={32} className="mx-auto text-[#3A3A3C] mb-2" />
            <p className="text-[#8E8E93]">No alarm history yet</p>
            <p className="text-[#3A3A3C] text-sm">Your wake-up data will appear here</p>
          </div>
        ) : (
          <div className="space-y-2">
            {history.slice(0, 10).map((entry, index) => (
              <div key={entry.id || index} className="bg-[#1C1C1E] rounded-[16px] p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    entry.action === 'dismissed' ? 'bg-[#32D74B]/20' :
                    entry.action === 'snoozed' ? 'bg-[#FF9F0A]/20' : 'bg-[#FF453A]/20'
                  } ${getActionColor(entry.action)}`}>
                    {getActionIcon(entry.action)}
                  </div>
                  <div>
                    <p className="font-medium">{entry.alarm_label || formatTime(entry.alarm_time)}</p>
                    <p className="text-xs text-[#8E8E93]">{formatDate(entry.timestamp)}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className={`text-sm font-medium capitalize ${getActionColor(entry.action)}`}>
                    {entry.action}
                  </p>
                  {entry.steps_taken > 0 && (
                    <p className="text-xs text-[#8E8E93]">{entry.steps_taken} steps</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

// Motion Calibration Page
const CalibrationPage = ({ onBack, calibration, onUpdateCalibration }) => {
  const [sensitivity, setSensitivity] = useState(calibration?.sensitivity || 12);
  const [minInterval, setMinInterval] = useState(calibration?.min_interval || 300);
  const [testSteps, setTestSteps] = useState(0);
  const [isTesting, setIsTesting] = useState(false);

  const handleSave = async () => {
    await onUpdateCalibration({ sensitivity, min_interval: minInterval });
  };

  const startTest = async () => {
    setIsTesting(true);
    setTestSteps(0);

    if (typeof DeviceMotionEvent !== 'undefined' && typeof DeviceMotionEvent.requestPermission === 'function') {
      try {
        await DeviceMotionEvent.requestPermission();
      } catch (e) {
        console.log('Motion permission denied');
      }
    }

    let lastAccel = null;
    let lastStepTime = 0;

    const handleMotion = (event) => {
      const { accelerationIncludingGravity } = event;
      if (!accelerationIncludingGravity) return;

      const { x, y, z } = accelerationIncludingGravity;
      const accel = Math.sqrt(x * x + y * y + z * z);

      if (lastAccel !== null) {
        const delta = Math.abs(accel - lastAccel);
        const now = Date.now();

        if (delta > sensitivity && now - lastStepTime > minInterval) {
          lastStepTime = now;
          setTestSteps(prev => prev + 1);
        }
      }
      lastAccel = accel;
    };

    window.addEventListener('devicemotion', handleMotion);

    setTimeout(() => {
      window.removeEventListener('devicemotion', handleMotion);
      setIsTesting(false);
    }, 10000);
  };

  return (
    <div className="app-container pb-32">
      <header className="pt-12 px-6 pb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={onBack} className="p-2 -ml-2 text-[#FF453A]" data-testid="calibration-back">
              <ChevronLeft size={28} />
            </button>
            <h1 className="font-display text-2xl font-semibold">Motion Calibration</h1>
          </div>
          <button onClick={handleSave} className="text-[#FF453A] font-medium" data-testid="calibration-save">
            Save
          </button>
        </div>
      </header>

      <main className="px-6">
        <div className="bg-[#1C1C1E] rounded-[20px] p-4 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Sliders size={20} className="text-[#FF9F0A]" />
            <span className="font-medium">Step Sensitivity</span>
          </div>
          <p className="text-sm text-[#8E8E93] mb-4">
            Lower values detect smaller movements. Increase if steps count too fast.
          </p>
          <div className="flex items-center gap-4">
            <span className="text-sm text-[#8E8E93]">Sensitive</span>
            <input
              type="range"
              min="5"
              max="25"
              step="1"
              value={sensitivity}
              onChange={(e) => setSensitivity(parseFloat(e.target.value))}
              className="flex-1 accent-[#FF453A]"
              data-testid="sensitivity-slider"
            />
            <span className="text-sm text-[#8E8E93]">Less</span>
          </div>
          <p className="text-center text-xl font-semibold mt-2">{sensitivity}</p>
        </div>

        <div className="bg-[#1C1C1E] rounded-[20px] p-4 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Clock size={20} className="text-[#FF9F0A]" />
            <span className="font-medium">Step Interval</span>
          </div>
          <p className="text-sm text-[#8E8E93] mb-4">
            Minimum time between steps. Increase if double-counting steps.
          </p>
          <div className="flex items-center gap-4">
            <span className="text-sm text-[#8E8E93]">Fast</span>
            <input
              type="range"
              min="100"
              max="600"
              step="50"
              value={minInterval}
              onChange={(e) => setMinInterval(parseInt(e.target.value))}
              className="flex-1 accent-[#FF453A]"
              data-testid="interval-slider"
            />
            <span className="text-sm text-[#8E8E93]">Slow</span>
          </div>
          <p className="text-center text-xl font-semibold mt-2">{minInterval}ms</p>
        </div>

        {/* Test Mode */}
        <div className="bg-[#1C1C1E] rounded-[20px] p-6 text-center">
          <Zap size={32} className="mx-auto text-[#FF9F0A] mb-3" />
          <h3 className="font-medium mb-2">Test Your Settings</h3>
          <p className="text-sm text-[#8E8E93] mb-4">
            Walk for 10 seconds to see if step detection matches your actual steps.
          </p>
          
          {isTesting ? (
            <div>
              <p className="font-display text-5xl font-bold gradient-text mb-2">{testSteps}</p>
              <p className="text-sm text-[#8E8E93]">Steps detected... keep walking!</p>
            </div>
          ) : (
            <button
              onClick={startTest}
              className="px-8 py-3 rounded-full font-semibold text-white"
              style={{ background: 'linear-gradient(135deg, #FF453A 0%, #FF9F0A 100%)' }}
              data-testid="start-test-button"
            >
              Start 10s Test
            </button>
          )}
          
          {!isTesting && testSteps > 0 && (
            <p className="text-sm text-[#32D74B] mt-3">Last test: {testSteps} steps detected</p>
          )}
        </div>
      </main>
    </div>
  );
};

// ============== SOCIAL CHALLENGES PAGE ==============

const SocialPage = ({ onBack, userId, onSetUserId }) => {
  const [user, setUser] = useState(null);
  const [friends, setFriends] = useState([]);
  const [challenges, setChallenges] = useState([]);
  const [showAddFriend, setShowAddFriend] = useState(false);
  const [showCreateChallenge, setShowCreateChallenge] = useState(false);
  const [friendCode, setFriendCode] = useState('');
  const [selectedFriend, setSelectedFriend] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newUsername, setNewUsername] = useState('');
  const [showSetup, setShowSetup] = useState(false);

  useEffect(() => {
    if (userId) {
      fetchUserData();
    } else {
      setShowSetup(true);
      setLoading(false);
    }
  }, [userId]);

  const fetchUserData = async () => {
    try {
      const userRes = await fetch(`${API_URL}/api/users/${userId}`);
      if (userRes.ok) {
        const userData = await userRes.json();
        setUser(userData.user);
        
        const friendsRes = await fetch(`${API_URL}/api/users/${userId}/friends`);
        const friendsData = await friendsRes.json();
        setFriends(friendsData.friends || []);
        
        const challengesRes = await fetch(`${API_URL}/api/challenges/user/${userId}`);
        const challengesData = await challengesRes.json();
        setChallenges(challengesData.challenges || []);
      } else {
        setShowSetup(true);
      }
    } catch (err) {
      console.error('Failed to fetch user data:', err);
      setShowSetup(true);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async (credentialResponse) => {
    try {
      const res = await fetch(`${API_URL}/api/auth/google`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: credentialResponse.credential })
      });
      const data = await res.json();
      if (data.user) {
        onSetUserId(data.user.id);
        setUser(data.user);
        setShowSetup(false);
      }
    } catch (err) {
      console.error('Failed to authenticate with Google:', err);
    }
  };

  const handleAddFriend = async () => {
    if (!friendCode.trim()) return;
    try {
      const res = await fetch(`${API_URL}/api/users/${userId}/friends?friend_code=${friendCode.trim()}`, {
        method: 'POST',
      });
      if (res.ok) {
        fetchUserData();
        setShowAddFriend(false);
        setFriendCode('');
      }
    } catch (err) {
      console.error('Failed to add friend:', err);
    }
  };

  const handleCreateChallenge = async (challengeType, targetValue) => {
    if (!selectedFriend) return;
    try {
      const res = await fetch(`${API_URL}/api/challenges`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          creator_id: userId,
          friend_id: selectedFriend.id,
          challenge_type: challengeType,
          target_value: targetValue,
          duration_days: 7,
        })
      });
      if (res.ok) {
        fetchUserData();
        setShowCreateChallenge(false);
        setSelectedFriend(null);
      }
    } catch (err) {
      console.error('Failed to create challenge:', err);
    }
  };

  const handleRespondChallenge = async (challengeId, action) => {
    try {
      await fetch(`${API_URL}/api/challenges/${challengeId}/respond`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ challenge_id: challengeId, action })
      });
      fetchUserData();
    } catch (err) {
      console.error('Failed to respond to challenge:', err);
    }
  };

  const copyFriendCode = () => {
    if (user?.friend_code) {
      navigator.clipboard.writeText(user.friend_code);
    }
  };

  const getChallengeTypeInfo = (type) => {
    const types = {
      'wake_streak': { icon: <Flame size={20} />, name: 'Wake Streak', unit: 'days' },
      'total_steps': { icon: <Footprints size={20} />, name: 'Total Steps', unit: 'steps' },
      'no_snooze': { icon: <Zap size={20} />, name: 'No Snooze', unit: 'days' },
      'early_bird': { icon: <Sun size={20} />, name: 'Early Bird', unit: 'wakes' },
    };
    return types[type] || { icon: <Trophy size={20} />, name: type, unit: '' };
  };

  if (loading) {
    return (
      <div className="app-container flex items-center justify-center">
        <div className="text-[#8E8E93]">Loading...</div>
      </div>
    );
  }

  // Setup screen for new users
  if (showSetup) {
    return (
      <div className="app-container pb-32">
        <header className="pt-12 px-6 pb-6">
          <div className="flex items-center gap-4">
            <button onClick={onBack} className="p-2 -ml-2 text-[#FF453A]" data-testid="social-back">
              <ChevronLeft size={28} />
            </button>
            <h1 className="font-display text-2xl font-semibold">Social</h1>
          </div>
        </header>
        <main className="px-6">
          <div className="text-center py-12">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-[#FF453A] to-[#FF9F0A] flex items-center justify-center">
              <Users size={40} className="text-white" />
            </div>
            <h2 className="font-display text-2xl font-semibold mb-2">Join the Community</h2>
            <p className="text-[#8E8E93] mb-8">Create a profile to challenge friends and compete!</p>
            
            <div className="flex justify-center">
              <GoogleLogin
                onSuccess={handleGoogleLogin}
                onError={() => console.log('Login Failed')}
                useOneTap
                theme="filled_blue"
                shape="pill"
              />
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="app-container pb-32">
      <header className="pt-12 px-6 pb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={onBack} className="p-2 -ml-2 text-[#FF453A]" data-testid="social-back">
              <ChevronLeft size={28} />
            </button>
            <h1 className="font-display text-2xl font-semibold">Social</h1>
          </div>
          <button onClick={() => setShowAddFriend(true)} className="p-2 text-[#FF453A]" data-testid="add-friend-button">
            <UserPlus size={24} />
          </button>
        </div>
      </header>

      <main className="px-6">
        {/* Friend Code Card */}
        <div className="bg-[#1C1C1E] rounded-[20px] p-4 mb-6">
          <p className="text-sm text-[#8E8E93] mb-2">Your Friend Code</p>
          <div className="flex items-center justify-between">
            <p className="font-display text-3xl font-bold tracking-wider">{user?.friend_code}</p>
            <button onClick={copyFriendCode} className="p-2 text-[#FF453A]" data-testid="copy-code-button">
              <Copy size={20} />
            </button>
          </div>
          <p className="text-xs text-[#3A3A3C] mt-2">Share this code with friends to connect</p>
        </div>

        {/* Active Challenges */}
        <section className="mb-8">
          <h2 className="text-sm text-[#8E8E93] uppercase tracking-wide mb-3">Active Challenges</h2>
          {challenges.filter(c => c.status === 'active').length === 0 ? (
            <div className="bg-[#1C1C1E] rounded-[20px] p-6 text-center">
              <Swords size={32} className="mx-auto text-[#3A3A3C] mb-2" />
              <p className="text-[#8E8E93]">No active challenges</p>
              <p className="text-xs text-[#3A3A3C]">Challenge a friend to compete!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {challenges.filter(c => c.status === 'active').map(challenge => {
                const info = getChallengeTypeInfo(challenge.challenge_type);
                const isCreator = challenge.creator_id === userId;
                const myProgress = isCreator ? challenge.creator_progress : challenge.friend_progress;
                const theirProgress = isCreator ? challenge.friend_progress : challenge.creator_progress;
                const opponentName = isCreator ? challenge.friend_name : challenge.creator_name;
                
                return (
                  <div key={challenge.id} className="bg-[#1C1C1E] rounded-[20px] p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="text-[#FF9F0A]">{info.icon}</div>
                      <div className="flex-1">
                        <p className="font-medium">{info.name}</p>
                        <p className="text-xs text-[#8E8E93]">vs {opponentName}</p>
                      </div>
                      <Trophy size={20} className={myProgress > theirProgress ? 'text-[#FFD60A]' : 'text-[#3A3A3C]'} />
                    </div>
                    <div className="flex justify-between text-sm">
                      <div>
                        <p className="text-[#32D74B] font-semibold">{myProgress} {info.unit}</p>
                        <p className="text-xs text-[#8E8E93]">You</p>
                      </div>
                      <div className="text-right">
                        <p className="text-[#FF453A] font-semibold">{theirProgress} {info.unit}</p>
                        <p className="text-xs text-[#8E8E93]">{opponentName}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Pending Challenges */}
        {challenges.filter(c => c.status === 'pending' && c.friend_id === userId).length > 0 && (
          <section className="mb-8">
            <h2 className="text-sm text-[#8E8E93] uppercase tracking-wide mb-3">Pending Invites</h2>
            <div className="space-y-3">
              {challenges.filter(c => c.status === 'pending' && c.friend_id === userId).map(challenge => {
                const info = getChallengeTypeInfo(challenge.challenge_type);
                return (
                  <div key={challenge.id} className="bg-[#1C1C1E] rounded-[20px] p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="text-[#FF9F0A]">{info.icon}</div>
                      <div className="flex-1">
                        <p className="font-medium">{info.name} Challenge</p>
                        <p className="text-xs text-[#8E8E93]">from {challenge.creator_name}</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleRespondChallenge(challenge.id, 'accept')}
                        className="flex-1 py-2 rounded-full text-sm font-medium bg-[#32D74B] text-white"
                      >
                        Accept
                      </button>
                      <button
                        onClick={() => handleRespondChallenge(challenge.id, 'decline')}
                        className="flex-1 py-2 rounded-full text-sm font-medium bg-[#2C2C2E] text-[#8E8E93]"
                      >
                        Decline
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Friends List */}
        <section>
          <h2 className="text-sm text-[#8E8E93] uppercase tracking-wide mb-3">Friends ({friends.length})</h2>
          {friends.length === 0 ? (
            <div className="bg-[#1C1C1E] rounded-[20px] p-6 text-center">
              <Users size={32} className="mx-auto text-[#3A3A3C] mb-2" />
              <p className="text-[#8E8E93]">No friends yet</p>
              <p className="text-xs text-[#3A3A3C]">Add friends using their code</p>
            </div>
          ) : (
            <div className="space-y-2">
              {friends.map(friend => (
                <div key={friend.id} className="bg-[#1C1C1E] rounded-[16px] p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#2C2C2E] flex items-center justify-center">
                      <span className="text-lg font-semibold">{friend.display_name[0]}</span>
                    </div>
                    <div>
                      <p className="font-medium">{friend.display_name}</p>
                      <p className="text-xs text-[#8E8E93]">{friend.weekly_streak} day streak · {friend.weekly_steps} steps</p>
                    </div>
                  </div>
                  <button
                    onClick={() => { setSelectedFriend(friend); setShowCreateChallenge(true); }}
                    className="px-3 py-1 rounded-full text-sm font-medium bg-[#FF453A] text-white"
                    data-testid={`challenge-${friend.id}`}
                  >
                    Challenge
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>

      {/* Add Friend Modal */}
      {showAddFriend && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-[#1C1C1E] rounded-[24px] p-6 w-full max-w-sm animate-slide-up">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-xl font-semibold">Add Friend</h2>
              <button onClick={() => setShowAddFriend(false)} className="text-[#8E8E93]"><X size={24} /></button>
            </div>
            <input
              type="text"
              value={friendCode}
              onChange={(e) => setFriendCode(e.target.value.toUpperCase())}
              placeholder="Enter friend code"
              maxLength={6}
              className="w-full bg-[#2C2C2E] rounded-xl p-4 text-white text-center text-2xl tracking-widest placeholder-[#3A3A3C] border-none outline-none mb-4 uppercase"
              data-testid="friend-code-input"
            />
            <button
              onClick={handleAddFriend}
              disabled={friendCode.length !== 6}
              className="w-full py-4 rounded-full font-semibold text-white disabled:opacity-50"
              style={{ background: 'linear-gradient(135deg, #FF453A 0%, #FF9F0A 100%)' }}
            >
              Add Friend
            </button>
          </div>
        </div>
      )}

      {/* Create Challenge Modal */}
      {showCreateChallenge && selectedFriend && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-end justify-center">
          <div className="bg-[#1C1C1E] rounded-t-[24px] p-6 w-full max-w-[430px] animate-slide-up">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-xl font-semibold">Challenge {selectedFriend.display_name}</h2>
              <button onClick={() => setShowCreateChallenge(false)} className="text-[#8E8E93]"><X size={24} /></button>
            </div>
            <div className="space-y-3">
              {[
                { type: 'wake_streak', name: 'Wake Streak', desc: 'Most consecutive wake-ups', target: 7, icon: <Flame size={24} /> },
                { type: 'total_steps', name: 'Step Champion', desc: 'Most total steps this week', target: 500, icon: <Footprints size={24} /> },
                { type: 'no_snooze', name: 'No Snooze Master', desc: 'Most days without snoozing', target: 5, icon: <Zap size={24} /> },
                { type: 'early_bird', name: 'Early Bird', desc: 'Most wakes before 7 AM', target: 5, icon: <Sun size={24} /> },
              ].map(challenge => (
                <button
                  key={challenge.type}
                  onClick={() => handleCreateChallenge(challenge.type, challenge.target)}
                  className="w-full bg-[#2C2C2E] rounded-[16px] p-4 flex items-center gap-4 text-left tap-scale"
                >
                  <div className="text-[#FF9F0A]">{challenge.icon}</div>
                  <div className="flex-1">
                    <p className="font-medium">{challenge.name}</p>
                    <p className="text-xs text-[#8E8E93]">{challenge.desc}</p>
                  </div>
                  <ChevronRight size={20} className="text-[#3A3A3C]" />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============== SLEEP INSIGHTS PAGE ==============

const InsightsPage = ({ onBack }) => {
  const [insights, setInsights] = useState(null);
  const [weeklySummary, setWeeklySummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchInsights();
    fetchWeeklySummary();
  }, []);

  const fetchInsights = async () => {
    try {
      const res = await fetch(`${API_URL}/api/insights`);
      const data = await res.json();
      setInsights(data.insights);
    } catch (err) {
      console.error('Failed to fetch insights:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchWeeklySummary = async () => {
    try {
      const res = await fetch(`${API_URL}/api/weekly-summary`);
      const data = await res.json();
      setWeeklySummary(data.summary);
    } catch (err) {
      console.error('Failed to fetch weekly summary:', err);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 85) return 'from-[#32D74B] to-[#30B0C7]';
    if (score >= 70) return 'from-[#32D74B] to-[#FFD60A]';
    if (score >= 50) return 'from-[#FFD60A] to-[#FF9F0A]';
    return 'from-[#FF9F0A] to-[#FF453A]';
  };

  const getRecommendationIcon = (type) => {
    switch(type) {
      case 'success': return <CheckCircle size={20} className="text-[#32D74B]" />;
      case 'warning': return <AlertCircle size={20} className="text-[#FF9F0A]" />;
      case 'tip': return <Sparkles size={20} className="text-[#30B0C7]" />;
      default: return <Info size={20} className="text-[#8E8E93]" />;
    }
  };

  const getAchievementIcon = (icon) => {
    switch(icon) {
      case 'trophy': return <Trophy size={24} className="text-[#FFD60A]" />;
      case 'fire': return <Flame size={24} className="text-[#FF9F0A]" />;
      case 'footprints': return <Footprints size={24} className="text-[#32D74B]" />;
      case 'zap': return <Zap size={24} className="text-[#30B0C7]" />;
      default: return <Award size={24} className="text-[#FF9F0A]" />;
    }
  };

  if (loading) {
    return (
      <div className="app-container flex items-center justify-center">
        <div className="text-[#8E8E93]">Analyzing your sleep patterns...</div>
      </div>
    );
  }

  return (
    <div className="app-container pb-32">
      <header className="pt-12 px-6 pb-6">
        <div className="flex items-center gap-4">
          <button onClick={onBack} className="p-2 -ml-2 text-[#FF453A]" data-testid="insights-back">
            <ChevronLeft size={28} />
          </button>
          <h1 className="font-display text-2xl font-semibold">Sleep Insights</h1>
        </div>
      </header>

      <main className="px-6">
        {/* Sleep Score Card */}
        <div className={`bg-gradient-to-br ${getScoreColor(insights?.sleep_score || 0)} rounded-[24px] p-6 mb-6`}>
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Brain size={20} className="text-white/80" />
                <p className="text-white/80 text-sm uppercase tracking-wide">Sleep Score</p>
              </div>
              <p className="font-display text-6xl font-bold text-white">
                {insights?.sleep_score || 0}
              </p>
              <p className="text-white/80 mt-1">{insights?.quality_rating || 'Unknown'}</p>
            </div>
            <div className="w-20 h-20 rounded-full bg-white/20 flex items-center justify-center">
              <Sparkles size={36} className="text-white" />
            </div>
          </div>
        </div>

        {/* Weekly Summary Card */}
        {weeklySummary && (
          <div className="bg-[#1C1C1E] rounded-[20px] p-4 mb-6">
            <div className="flex items-center gap-2 mb-3">
              <Calendar size={18} className="text-[#FF9F0A]" />
              <span className="text-sm text-[#8E8E93]">{weeklySummary.week_start} - {weeklySummary.week_end}</span>
            </div>
            <p className="font-display text-xl font-semibold mb-1">{weeklySummary.headline}</p>
            <p className="text-sm text-[#8E8E93]">{weeklySummary.message}</p>
          </div>
        )}

        {/* Achievements */}
        {insights?.achievements?.length > 0 && (
          <section className="mb-6">
            <h2 className="text-sm text-[#8E8E93] uppercase tracking-wide mb-3">Achievements</h2>
            <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
              {insights.achievements.map((achievement, index) => (
                <div key={index} className="bg-[#1C1C1E] rounded-[16px] p-4 min-w-[140px] text-center">
                  <div className="mb-2">{getAchievementIcon(achievement.icon)}</div>
                  <p className="font-medium text-sm">{achievement.title}</p>
                  <p className="text-xs text-[#8E8E93]">{achievement.description}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Patterns */}
        {insights?.patterns?.length > 0 && (
          <section className="mb-6">
            <h2 className="text-sm text-[#8E8E93] uppercase tracking-wide mb-3">Patterns Detected</h2>
            <div className="space-y-2">
              {insights.patterns.map((pattern, index) => (
                <div key={index} className="bg-[#1C1C1E] rounded-[16px] p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">{pattern.title}</p>
                      <p className="text-xs text-[#8E8E93]">{pattern.description}</p>
                    </div>
                    <p className="font-display text-xl font-semibold text-[#FF9F0A]">{pattern.value}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Trends */}
        {insights?.trends && (
          <section className="mb-6">
            <h2 className="text-sm text-[#8E8E93] uppercase tracking-wide mb-3">This Week's Trends</h2>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-[#1C1C1E] rounded-[16px] p-3">
                <p className="text-xs text-[#8E8E93]">Success Rate</p>
                <p className="font-display text-2xl font-semibold text-[#32D74B]">{insights.trends.success_rate}%</p>
              </div>
              <div className="bg-[#1C1C1E] rounded-[16px] p-3">
                <p className="text-xs text-[#8E8E93]">Avg Snoozes</p>
                <p className="font-display text-2xl font-semibold text-[#FF9F0A]">{insights.trends.avg_snoozes}</p>
              </div>
              <div className="bg-[#1C1C1E] rounded-[16px] p-3">
                <p className="text-xs text-[#8E8E93]">Time to Wake</p>
                <p className="font-display text-2xl font-semibold">{Math.round(insights.trends.avg_time_to_wake / 60)}m</p>
              </div>
              <div className="bg-[#1C1C1E] rounded-[16px] p-3">
                <p className="text-xs text-[#8E8E93]">Current Streak</p>
                <p className="font-display text-2xl font-semibold text-[#FFD60A]">{insights.trends.streak} days</p>
              </div>
            </div>
          </section>
        )}

        {/* Recommendations */}
        {insights?.recommendations?.length > 0 && (
          <section>
            <h2 className="text-sm text-[#8E8E93] uppercase tracking-wide mb-3">Recommendations</h2>
            <div className="space-y-2">
              {insights.recommendations.map((rec, index) => (
                <div key={index} className="bg-[#1C1C1E] rounded-[16px] p-4 flex gap-3">
                  {getRecommendationIcon(rec.type)}
                  <div>
                    <p className="font-medium">{rec.title}</p>
                    <p className="text-sm text-[#8E8E93]">{rec.message}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
};

// Sound Upload Modal
const SoundUploadModal = ({ onClose, onUpload }) => {
  const [name, setName] = useState('');
  const [file, setFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      if (!name) {
        setName(selectedFile.name.replace(/\.[^/.]+$/, ''));
      }
    }
  };

  const handleUpload = async () => {
    if (!file || !name) return;
    setIsUploading(true);
    const reader = new FileReader();
    reader.onload = async (e) => {
      const base64 = e.target.result.split(',')[1];
      await onUpload({ name, data: base64, mime_type: file.type });
      setIsUploading(false);
      onClose();
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
      <div className="bg-[#1C1C1E] rounded-[24px] p-6 w-full max-w-sm animate-slide-up" data-testid="sound-upload-modal">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-display text-xl font-semibold">Import Sound</h2>
          <button onClick={onClose} className="text-[#8E8E93]" data-testid="sound-upload-close">
            <X size={24} />
          </button>
        </div>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Sound name"
          className="w-full bg-[#2C2C2E] rounded-xl p-4 text-white placeholder-[#3A3A3C] border-none outline-none mb-4"
          data-testid="sound-name-input"
        />
        <div className="audio-upload-btn bg-[#2C2C2E] rounded-xl p-4 mb-4 text-center">
          <input type="file" accept="audio/*" onChange={handleFileChange} data-testid="sound-file-input" />
          <div className="flex items-center justify-center gap-2 text-[#8E8E93]">
            <Upload size={20} />
            <span>{file ? file.name : 'Choose audio file'}</span>
          </div>
        </div>
        <button
          onClick={handleUpload}
          disabled={!file || !name || isUploading}
          className="w-full py-4 rounded-full font-semibold text-white disabled:opacity-50"
          style={{ background: 'linear-gradient(135deg, #FF453A 0%, #FF9F0A 100%)' }}
          data-testid="sound-upload-button"
        >
          {isUploading ? 'Uploading...' : 'Import Sound'}
        </button>
      </div>
    </div>
  );
};

// Settings Page Component
const SettingsPage = ({ onBack, settings, onUpdateSettings, sounds, onDeleteSound, onOpenCalibration }) => {
  const [notificationsEnabled, setNotificationsEnabled] = useState(settings.notificationsEnabled);
  const [preAlarmMinutes, setPreAlarmMinutes] = useState(settings.preAlarmMinutes || 30);
  const [defaultSteps, setDefaultSteps] = useState(settings.defaultSteps || 50);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
    }
    const handleBeforeInstall = (e) => { e.preventDefault(); setDeferredPrompt(e); };
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') setIsInstalled(true);
      setDeferredPrompt(null);
    }
  };

  const handleNotificationToggle = async (enabled) => {
    if (enabled) {
      const granted = await requestNotificationPermission();
      if (granted) {
        setNotificationsEnabled(true);
        onUpdateSettings({ ...settings, notificationsEnabled: true });
        sendNotification('WalkUp', 'Notifications enabled!');
      }
    } else {
      setNotificationsEnabled(false);
      onUpdateSettings({ ...settings, notificationsEnabled: false });
    }
  };

  return (
    <div className="app-container pb-32">
      <header className="pt-12 px-6 pb-6">
        <div className="flex items-center gap-4">
          <button onClick={onBack} className="p-2 -ml-2 text-[#FF453A]" data-testid="settings-back">
            <ChevronLeft size={28} />
          </button>
          <h1 className="font-display text-2xl font-semibold">Settings</h1>
        </div>
      </header>

      <main className="px-6">
        {!isInstalled && (
          <section className="mb-8">
            <h2 className="text-sm text-[#8E8E93] uppercase tracking-wide mb-3">Install App</h2>
            <div className="bg-[#1C1C1E] rounded-[20px] p-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#FF453A] to-[#FF9F0A] flex items-center justify-center">
                  <Smartphone size={24} className="text-white" />
                </div>
                <div className="flex-1">
                  <p className="font-medium">Add to Home Screen</p>
                  <p className="text-sm text-[#8E8E93]">Install WalkUp for the best experience</p>
                </div>
                <button onClick={handleInstall} disabled={!deferredPrompt}
                  className="px-4 py-2 bg-[#FF453A] rounded-full text-sm font-medium disabled:opacity-50" data-testid="install-app-button">
                  <Download size={18} />
                </button>
              </div>
            </div>
          </section>
        )}

        <section className="mb-8">
          <h2 className="text-sm text-[#8E8E93] uppercase tracking-wide mb-3">Notifications</h2>
          <div className="bg-[#1C1C1E] rounded-[20px] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-white/5">
              <div className="flex items-center gap-3">
                <Bell size={20} className="text-[#8E8E93]" />
                <span>Pre-alarm Notification</span>
              </div>
              <Switch checked={notificationsEnabled} onChange={handleNotificationToggle} testId="notification-toggle" />
            </div>
            {notificationsEnabled && (
              <div className="p-4">
                <label className="text-sm text-[#8E8E93] mb-2 block">Notify before alarm</label>
                <select value={preAlarmMinutes}
                  onChange={(e) => { setPreAlarmMinutes(parseInt(e.target.value)); onUpdateSettings({ ...settings, preAlarmMinutes: parseInt(e.target.value) }); }}
                  className="w-full bg-[#2C2C2E] rounded-xl p-3 text-white border-none" data-testid="pre-alarm-select">
                  <option value={15}>15 minutes</option>
                  <option value={30}>30 minutes</option>
                  <option value={45}>45 minutes</option>
                  <option value={60}>1 hour</option>
                </select>
              </div>
            )}
          </div>
        </section>

        <section className="mb-8">
          <h2 className="text-sm text-[#8E8E93] uppercase tracking-wide mb-3">Defaults</h2>
          <div className="bg-[#1C1C1E] rounded-[20px] p-4">
            <label className="text-sm text-[#8E8E93] mb-2 block">Default steps to dismiss</label>
            <div className="flex items-center gap-4">
              <input type="range" min="10" max="200" step="10" value={defaultSteps}
                onChange={(e) => { setDefaultSteps(parseInt(e.target.value)); onUpdateSettings({ ...settings, defaultSteps: parseInt(e.target.value) }); }}
                className="flex-1 accent-[#FF453A]" data-testid="default-steps-slider" />
              <span className="font-display text-xl font-semibold text-white w-12 text-right">{defaultSteps}</span>
            </div>
          </div>
        </section>

        <section className="mb-8">
          <h2 className="text-sm text-[#8E8E93] uppercase tracking-wide mb-3">Motion Calibration</h2>
          <button onClick={onOpenCalibration}
            className="w-full bg-[#1C1C1E] rounded-[20px] p-4 flex items-center justify-between" data-testid="open-calibration">
            <div className="flex items-center gap-3">
              <Sliders size={20} className="text-[#8E8E93]" />
              <span>Calibrate Step Detection</span>
            </div>
            <ChevronRight size={20} className="text-[#8E8E93]" />
          </button>
        </section>

        <section className="mb-8">
          <h2 className="text-sm text-[#8E8E93] uppercase tracking-wide mb-3">Custom Sounds</h2>
          <div className="bg-[#1C1C1E] rounded-[20px] overflow-hidden">
            {sounds.length === 0 ? (
              <div className="p-6 text-center">
                <Volume2 size={32} className="mx-auto text-[#3A3A3C] mb-2" />
                <p className="text-[#8E8E93] text-sm">No custom sounds</p>
              </div>
            ) : (
              sounds.map((sound, index) => (
                <div key={sound.id} className={`flex items-center justify-between p-4 ${index < sounds.length - 1 ? 'border-b border-white/5' : ''}`}>
                  <div className="flex items-center gap-3">
                    <Volume2 size={20} className="text-[#8E8E93]" />
                    <span>{sound.name}</span>
                  </div>
                  <button onClick={() => onDeleteSound(sound.id)} className="p-2 text-[#FF453A]" data-testid={`delete-sound-${sound.id}`}>
                    <Trash2 size={18} />
                  </button>
                </div>
              ))
            )}
          </div>
        </section>

        <section>
          <h2 className="text-sm text-[#8E8E93] uppercase tracking-wide mb-3">About</h2>
          <div className="bg-[#1C1C1E] rounded-[20px] p-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#FF453A] to-[#FF9F0A] flex items-center justify-center">
                <Footprints size={24} className="text-white" />
              </div>
              <div>
                <p className="font-display font-semibold text-lg">WalkUp</p>
                <p className="text-sm text-[#8E8E93]">Version 1.1.0</p>
              </div>
            </div>
            <p className="text-sm text-[#8E8E93]">
              Wake up by walking! A motivating alarm app that requires you to take steps to dismiss.
            </p>
          </div>
        </section>
      </main>
    </div>
  );
};

// Sounds Page
const SoundsPage = ({ onBack, sounds, onUploadSound, onDeleteSound }) => {
  const [showUpload, setShowUpload] = useState(false);
  const [playingId, setPlayingId] = useState(null);
  const audioRef = useRef(null);

  const playSound = async (soundId) => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    if (playingId === soundId) { setPlayingId(null); return; }
    try {
      const res = await fetch(`${API_URL}/api/sounds/${soundId}`);
      const data = await res.json();
      if (data.sound && data.sound.data) {
        const audio = new Audio(`data:${data.sound.mime_type};base64,${data.sound.data}`);
        audio.onended = () => setPlayingId(null);
        audio.play();
        audioRef.current = audio;
        setPlayingId(soundId);
      }
    } catch (err) { console.error('Failed to play sound:', err); }
  };

  return (
    <div className="app-container pb-32">
      <header className="pt-12 px-6 pb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={onBack} className="p-2 -ml-2 text-[#FF453A]" data-testid="sounds-back">
              <ChevronLeft size={28} />
            </button>
            <h1 className="font-display text-2xl font-semibold">Sounds</h1>
          </div>
          <button onClick={() => setShowUpload(true)} className="p-2 text-[#FF453A]" data-testid="add-sound-button">
            <Plus size={28} />
          </button>
        </div>
      </header>
      <main className="px-6">
        {sounds.length === 0 ? (
          <div className="text-center py-16">
            <Volume2 size={48} className="mx-auto text-[#3A3A3C] mb-4" />
            <p className="text-[#8E8E93]">No custom sounds</p>
            <p className="text-[#3A3A3C] text-sm">Tap + to import your first sound</p>
          </div>
        ) : (
          <div className="space-y-3">
            {sounds.map(sound => (
              <div key={sound.id} className="bg-[#1C1C1E] rounded-[20px] p-4 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <button onClick={() => playSound(sound.id)}
                    className="w-12 h-12 rounded-full bg-[#2C2C2E] flex items-center justify-center" data-testid={`play-sound-${sound.id}`}>
                    {playingId === sound.id ? <Pause size={20} className="text-[#FF453A]" /> : <Play size={20} className="text-[#FF453A] ml-1" />}
                  </button>
                  <span className="font-medium">{sound.name}</span>
                </div>
                <button onClick={() => onDeleteSound(sound.id)} className="p-2 text-[#FF453A]" data-testid={`delete-sound-${sound.id}`}>
                  <Trash2 size={20} />
                </button>
              </div>
            ))}
          </div>
        )}
      </main>
      {showUpload && <SoundUploadModal onClose={() => setShowUpload(false)} onUpload={onUploadSound} />}
    </div>
  );
};

// Notification Toast
const NotificationToast = ({ message, onClose }) => (
  <div className="notification-toast glass animate-toast" data-testid="notification-toast">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3">
        <Bell size={20} className="text-[#FF9F0A]" />
        <span className="text-sm">{message}</span>
      </div>
      <button onClick={onClose} className="text-[#8E8E93]"><X size={18} /></button>
    </div>
  </div>
);

// ============== MAIN APP ==============
function App() {
  const [alarms, setAlarms] = useState([]);
  const [sounds, setSounds] = useState([]);
  const [showAlarmModal, setShowAlarmModal] = useState(false);
  const [editingAlarm, setEditingAlarm] = useState(null);
  const [activeAlarm, setActiveAlarm] = useState(null);
  const [notification, setNotification] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [currentPage, setCurrentPage] = useState('alarms');
  const [motionCalibration, setMotionCalibration] = useState(() => {
    const saved = localStorage.getItem('walkup_calibration');
    return saved ? JSON.parse(saved) : { sensitivity: 12, min_interval: 300 };
  });
  const [userId, setUserId] = useState(() => localStorage.getItem('walkup_user_id') || null);
  const [settings, setSettings] = useState(() => {
    const saved = localStorage.getItem('walkup_settings');
    return saved ? JSON.parse(saved) : {
      notificationsEnabled: Notification.permission === 'granted',
      preAlarmMinutes: 30,
      defaultSteps: 50,
    };
  });

  useEffect(() => {
    localStorage.setItem('walkup_settings', JSON.stringify(settings));
  }, [settings]);

  useEffect(() => {
    localStorage.setItem('walkup_calibration', JSON.stringify(motionCalibration));
  }, [motionCalibration]);
  const notifiedAlarmsRef = useRef(new Set());
  const snoozeTimerRef = useRef(null);

  useEffect(() => {
    fetchAlarms();
    fetchSounds();
    fetchCalibration();
    if (Notification.permission === 'default') requestNotificationPermission();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const checkAlarms = () => {
      const now = new Date();
      const currentTimeStr = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
      const currentDay = now.getDay();

      alarms.forEach(alarm => {
        if (!alarm.enabled) return;
        const shouldTrigger = alarm.time === currentTimeStr && 
          (alarm.recurring_days.length === 0 || alarm.recurring_days.includes(currentDay));
        if (shouldTrigger && now.getSeconds() === 0) setActiveAlarm(alarm);

        const [alarmHours, alarmMinutes] = alarm.time.split(':').map(Number);
        const alarmDate = new Date(now);
        alarmDate.setHours(alarmHours, alarmMinutes, 0, 0);
        if (alarmDate < now) alarmDate.setDate(alarmDate.getDate() + 1);
        
        const diffMs = alarmDate - now;
        const diffMins = diffMs / (1000 * 60);
        const preAlarmMins = settings.preAlarmMinutes || 30;
        const notifKey = `${alarm.id}-${alarmDate.toDateString()}-${preAlarmMins}`;

        if (settings.notificationsEnabled && diffMins >= preAlarmMins - 0.5 && diffMins <= preAlarmMins + 0.5 && !notifiedAlarmsRef.current.has(notifKey)) {
          notifiedAlarmsRef.current.add(notifKey);
          const message = `"${alarm.label || 'Alarm'}" in ${preAlarmMins} minutes`;
          showNotificationToast(message);
          sendNotification('WalkUp - Upcoming Alarm', message);
          setTimeout(() => notifiedAlarmsRef.current.delete(notifKey), 24 * 60 * 60 * 1000);
        }
      });
    };
    const interval = setInterval(checkAlarms, 1000);
    return () => clearInterval(interval);
  }, [alarms, settings]);

  const fetchAlarms = async () => {
    try { const res = await fetch(`${API_URL}/api/alarms`); const data = await res.json(); setAlarms(data.alarms || []); }
    catch (err) { console.error('Failed to fetch alarms:', err); }
  };

  const fetchSounds = async () => {
    try { const res = await fetch(`${API_URL}/api/sounds`); const data = await res.json(); setSounds(data.sounds || []); }
    catch (err) { console.error('Failed to fetch sounds:', err); }
  };

  const fetchCalibration = async () => {
    try { const res = await fetch(`${API_URL}/api/calibration`); const data = await res.json(); setMotionCalibration(data.calibration || { sensitivity: 12, min_interval: 300 }); }
    catch (err) { console.error('Failed to fetch calibration:', err); }
  };

  const handleSaveAlarm = async (alarmData) => {
    try {
      if (alarmData.id) {
        await fetch(`${API_URL}/api/alarms/${alarmData.id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(alarmData) });
      } else {
        await fetch(`${API_URL}/api/alarms`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(alarmData) });
      }
      fetchAlarms();
      setShowAlarmModal(false);
      setEditingAlarm(null);
    } catch (err) { console.error('Failed to save alarm:', err); }
  };

  const handleToggleAlarm = async (id, enabled) => {
    try { await fetch(`${API_URL}/api/alarms/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ enabled }) }); fetchAlarms(); }
    catch (err) { console.error('Failed to toggle alarm:', err); }
  };

  const handleDeleteAlarm = async (id) => {
    try { await fetch(`${API_URL}/api/alarms/${id}`, { method: 'DELETE' }); fetchAlarms(); setShowAlarmModal(false); setEditingAlarm(null); }
    catch (err) { console.error('Failed to delete alarm:', err); }
  };

  const handleUploadSound = async (soundData) => {
    try { await fetch(`${API_URL}/api/sounds`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(soundData) }); fetchSounds(); }
    catch (err) { console.error('Failed to upload sound:', err); }
  };

  const handleDeleteSound = async (id) => {
    try { await fetch(`${API_URL}/api/sounds/${id}`, { method: 'DELETE' }); fetchSounds(); }
    catch (err) { console.error('Failed to delete sound:', err); }
  };

  const handleUpdateCalibration = async (calibration) => {
    try { await fetch(`${API_URL}/api/calibration`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(calibration) }); setMotionCalibration(calibration); setCurrentPage('settings'); }
    catch (err) { console.error('Failed to update calibration:', err); }
  };

  const handleDismissAlarm = async (historyEntry) => {
    try { await fetch(`${API_URL}/api/history`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(historyEntry) }); }
    catch (err) { console.error('Failed to save history:', err); }
    setActiveAlarm(null);
  };

  const handleSnoozeAlarm = async (historyEntry, snoozeMinutes) => {
    try { await fetch(`${API_URL}/api/history`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(historyEntry) }); }
    catch (err) { console.error('Failed to save history:', err); }
    
    setActiveAlarm(null);
    showNotificationToast(`Snoozed for ${snoozeMinutes} minutes`);
    
    if (snoozeTimerRef.current) clearTimeout(snoozeTimerRef.current);
    snoozeTimerRef.current = setTimeout(() => {
      const alarm = alarms.find(a => a.id === historyEntry.alarm_id);
      if (alarm) setActiveAlarm(alarm);
    }, snoozeMinutes * 60 * 1000);
  };

  const handleSetUserId = (id) => {
    setUserId(id);
    localStorage.setItem('walkup_user_id', id);
  };

  const showNotificationToast = (message) => { setNotification(message); setTimeout(() => setNotification(null), 5000); };

  const formatCurrentTime = () => {
    const hours = currentTime.getHours();
    const minutes = currentTime.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHour = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
    return { time: `${displayHour}:${String(minutes).padStart(2, '0')}`, ampm, date: currentTime.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' }) };
  };

  const { time: displayTime, ampm: displayAmpm, date: displayDate } = formatCurrentTime();

  if (activeAlarm) {
    return <ActiveAlarmScreen alarm={activeAlarm} onDismiss={handleDismissAlarm} onSnooze={handleSnoozeAlarm} sounds={sounds} motionCalibration={motionCalibration} />;
  }

  if (currentPage === 'settings') return <SettingsPage onBack={() => setCurrentPage('alarms')} settings={settings} onUpdateSettings={setSettings} sounds={sounds} onDeleteSound={handleDeleteSound} onOpenCalibration={() => setCurrentPage('calibration')} />;
  if (currentPage === 'sounds') return <SoundsPage onBack={() => setCurrentPage('alarms')} sounds={sounds} onUploadSound={handleUploadSound} onDeleteSound={handleDeleteSound} />;
  if (currentPage === 'statistics') return <StatisticsPage onBack={() => setCurrentPage('alarms')} />;
  if (currentPage === 'calibration') return <CalibrationPage onBack={() => setCurrentPage('settings')} calibration={motionCalibration} onUpdateCalibration={handleUpdateCalibration} />;
  if (currentPage === 'social') return <SocialPage onBack={() => setCurrentPage('alarms')} userId={userId} onSetUserId={handleSetUserId} />;
  if (currentPage === 'insights') return <InsightsPage onBack={() => setCurrentPage('alarms')} />;

  return (
    <div className="app-container pb-32">
      <div className="fixed inset-0 opacity-5 pointer-events-none" style={{ backgroundImage: `url("https://static.prod-images.emergentagent.com/jobs/27c46002-d378-4125-9f9d-a812574463dc/images/02300637f6ac9b3899f44885664178b32862744694b8280e9d88824a93e718e8.png")`, backgroundSize: 'cover' }} />
      {notification && <NotificationToast message={notification} onClose={() => setNotification(null)} />}
      
      <header className="pt-12 px-6 pb-8 relative">
        <div className="flex items-center justify-between mb-6">
          <h1 className="font-display text-2xl font-semibold">WalkUp</h1>
          <div className="flex items-center gap-3">
            <button onClick={() => setCurrentPage('insights')} className="p-2 text-[#8E8E93] hover:text-white transition-colors" data-testid="insights-button">
              <Brain size={22} />
            </button>
            <button onClick={() => setCurrentPage('social')} className="p-2 text-[#8E8E93] hover:text-white transition-colors" data-testid="social-button">
              <Users size={22} />
            </button>
            <button onClick={() => setCurrentPage('statistics')} className="p-2 text-[#8E8E93] hover:text-white transition-colors" data-testid="stats-button">
              <BarChart3 size={22} />
            </button>
            <button onClick={() => alarms.length > 0 && setActiveAlarm(alarms[0])} className="p-2 text-[#8E8E93] hover:text-white transition-colors" data-testid="test-alarm-button">
              <Bell size={22} />
            </button>
          </div>
        </div>
        <div className="text-center py-8">
          <p className="text-[#8E8E93] text-sm mb-1">{displayDate}</p>
          <div className="flex items-baseline justify-center gap-2">
            <span className="font-display text-7xl font-semibold gradient-text">{displayTime}</span>
            <span className="text-2xl text-[#8E8E93]">{displayAmpm}</span>
          </div>
        </div>
      </header>

      <main className="px-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-lg font-medium text-[#8E8E93]">Alarms</h2>
          <span className="text-sm text-[#3A3A3C]">{alarms.filter(a => a.enabled).length} active</span>
        </div>
        {alarms.length === 0 ? (
          <div className="text-center py-16">
            <Moon size={48} className="mx-auto text-[#3A3A3C] mb-4" />
            <p className="text-[#8E8E93]">No alarms yet</p>
            <p className="text-[#3A3A3C] text-sm">Tap + to create your first alarm</p>
          </div>
        ) : (
          <div className="space-y-4 stagger-children" data-testid="alarms-list">
            {alarms.map(alarm => <AlarmCard key={alarm.id} alarm={alarm} onToggle={handleToggleAlarm} onEdit={(a) => { setEditingAlarm(a); setShowAlarmModal(true); }} />)}
          </div>
        )}
      </main>

      <button className="fab tap-scale" onClick={() => { setEditingAlarm(null); setShowAlarmModal(true); }} data-testid="add-alarm-button">
        <Plus size={32} className="text-white" />
      </button>

      <nav className="fixed bottom-0 left-0 right-0 glass py-4 px-8 max-w-[430px] mx-auto">
        <div className="flex items-center justify-around">
          <button onClick={() => setCurrentPage('alarms')} className={`flex flex-col items-center gap-1 ${currentPage === 'alarms' ? 'text-[#FF453A]' : 'text-[#8E8E93]'}`} data-testid="nav-alarms">
            <Clock size={24} /><span className="text-xs">Alarms</span>
          </button>
          <button onClick={() => setCurrentPage('sounds')} className={`flex flex-col items-center gap-1 ${currentPage === 'sounds' ? 'text-[#FF453A]' : 'text-[#8E8E93]'}`} data-testid="nav-sounds">
            <Volume2 size={24} /><span className="text-xs">Sounds</span>
          </button>
          <button onClick={() => setCurrentPage('settings')} className={`flex flex-col items-center gap-1 ${currentPage === 'settings' ? 'text-[#FF453A]' : 'text-[#8E8E93]'}`} data-testid="nav-settings">
            <Settings size={24} /><span className="text-xs">Settings</span>
          </button>
        </div>
      </nav>

      {showAlarmModal && <AlarmModal alarm={editingAlarm} sounds={sounds} onSave={handleSaveAlarm} onDelete={handleDeleteAlarm} onClose={() => { setShowAlarmModal(false); setEditingAlarm(null); }} />}
    </div>
  );
}

export default App;
